import { useState, useCallback } from "react";
import { uploadCvtLogsService } from "../services/uploadCvtLogsService";
import { formatDateTimeForUpload } from "../../utils/textUtils";

export const useUploadCvtLogs = ({ showGeneralMessage }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [previewData, setPreviewData] = useState([]);

  const parseCvtDate = (value) => {
    if (!value) return null;
    console.log({ value });
    try {
      const cleanedStr = value.replace(/"/g, '').replace(/\s+/g, ' ').trim();
      const standardDate = new Date(cleanedStr);
      if (!isNaN(standardDate.getTime()) && cleanedStr.includes('-')) return standardDate;

      const parts = cleanedStr.split(' ');
      if (parts.length >= 2) {
        const [datePart, timePart, ampm] = parts;
        const [day, month, year] = datePart.split('/');
        let [hours, minutes, seconds] = timePart.split(':');

        let h = parseInt(hours);
        if (ampm?.toLowerCase() === 'pm' && h < 12) h += 12;
        if (ampm?.toLowerCase() === 'am' && h === 12) h = 0;

        const d = new Date(year, month - 1, day, h, minutes, parseInt(seconds || 0));
        return isNaN(d.getTime()) ? null : d;
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  const handleSearch = (val) => {
    if (!val) return setData(originalData);
    const low = val.toLowerCase();
    setData(originalData.filter(i =>
      i.fullName?.toLowerCase().includes(low) ||
      i.employeeId?.toString().includes(low)
    ));
  };

  const fetchRecords = useCallback(async (startDate, endDate) => {
    setLoading(true);
    try {
      const res = await uploadCvtLogsService.getRecordsRange(startDate, endDate);
      setData(res.data || []);
      setOriginalData(res.data || []);
    } catch (err) {
      showGeneralMessage("Failed to retrieve CVT records", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  const processCsvFile = async (file) => {
    setLoading(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const rows = text.split(/\r?\n/).filter(row => row.trim() !== "");
        const logs = [];

        const empRes = await uploadCvtLogsService.getEmployeeMasterlist();
        const employees = empRes.data;

        rows.forEach((rowText) => {
          const columns = rowText.split(',').map(col => col.trim());
          const deviceNo = columns[0]?.replace(/"/g, '');
          const rawDateValue = columns[2]?.replace(/"/g, '');
          const rawType = columns[3]?.replace(/"/g, '')?.toUpperCase();

          if (!deviceNo || !rawDateValue) return;

          const employee = employees.find(e => String(e.deviceNumber) === String(deviceNo));
          const d = parseCvtDate(rawDateValue);

          // MODIFIED: Create logEntry even if errors exist so they show in preview
          const logEntry = {
            employeeId: employee?.employeeId || deviceNo,
            fullName: employee ? `${employee.lastName}, ${employee.firstName}` : `Unknown (Device: ${deviceNo})`,
            dateTimeLog: formatDateTimeForUpload(d),
            recordType: rawType,
            deviceSerialNumber: "CVT-CSV-UPLOAD",
            errors: null
          };

          if (!employee) logEntry.errors = `Device No ${deviceNo} not found`;
          else if (!d) logEntry.errors = "Invalid Date Format";

          logs.push(logEntry);
        });

        // Sort by EmployeeId first, then DateTimeLog
        logs.sort((a, b) => {
          const employeeIdCompare = String(a.employeeId).localeCompare(
            String(b.employeeId),
            undefined,
            {
              numeric: true,
              sensitivity: "base"
            }
          );

          if (employeeIdCompare !== 0) {
            return employeeIdCompare;
          }

          return new Date(a.dateTimeLog) - new Date(b.dateTimeLog);
        });

console.log({logs});

        setPreviewData(logs);
      } catch (err) {
        showGeneralMessage("Error parsing CSV file", false);
      } finally {
        setLoading(false);
      }
    };

    reader.readAsText(file);
  };

  const uploadBulkData = async (payload) => {
    setLoading(true);
    try {
      // MODIFIED: Filter out records with errors before sending to backend
      const validPayload = payload.filter(item => !item.errors);

      if (validPayload.length === 0) {
        showGeneralMessage("No valid records to sync", false);
        setLoading(false);
        return false;
      }

      const formattedPayload = validPayload.map(item => ({
        EmployeeId: item.employeeId,
        Date: item.dateTimeLog,
        RecordType: item.recordType
      }));

      await uploadCvtLogsService.bulkUpload(formattedPayload);

      const skippedCount = payload.length - validPayload.length;
      const successMsg = skippedCount > 0
        ? `Synced ${validPayload.length} logs. ${skippedCount} ignored due to errors.`
        : "CVT Logs synced successfully!";

      showGeneralMessage(successMsg, true);
      return true;
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "Sync failed", false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { loading, data, previewData, setPreviewData, processCsvFile, fetchRecords, uploadBulkData, handleSearch };
};